import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import { CheckCircle2, Circle, Upload, BookText, Users, X, Sparkles } from "lucide-react";

interface Props {
  athleteId: string;
  mediaCount: number;
  journalCount: number;
  familyCount: number;
  onUploadClick: () => void;
  onJournalClick: () => void;
}

export default function GettingStartedChecklist({
  athleteId,
  mediaCount,
  journalCount,
  familyCount,
  onUploadClick,
  onJournalClick,
}: Props) {
  const storageKey = `ll_checklist_done_${athleteId}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === "1"
  );
  const [allDoneVisible, setAllDoneVisible] = useState(false);

  const steps = [
    {
      id: "media",
      done: mediaCount > 0,
      icon: Upload,
      title: "Upload your first memory",
      description: "Add a photo or video to start building your athlete's story.",
      action: (
        <Button
          onClick={onUploadClick}
          size="sm"
          className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold text-xs w-full"
        >
          Upload now
        </Button>
      ),
    },
    {
      id: "journal",
      done: journalCount > 0,
      icon: BookText,
      title: "Write your first journal entry",
      description: "Capture what's happening on and off the field.",
      action: (
        <Button
          onClick={onJournalClick}
          size="sm"
          className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold text-xs w-full"
        >
          Write entry
        </Button>
      ),
    },
    {
      id: "family",
      done: familyCount > 0,
      icon: Users,
      title: "Invite someone to the Loop",
      description: "Share the journey with grandparents, coaches, or family.",
      action: (
        <Link href="/family">
          <Button
            size="sm"
            className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold text-xs w-full"
          >
            Invite now
          </Button>
        </Link>
      ),
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const allDone = completedCount === steps.length;

  const dismiss = () => {
    localStorage.setItem(storageKey, "1");
    setDismissed(true);
  };

  // When all steps complete, show a brief celebration then auto-dismiss
  useEffect(() => {
    if (allDone && !dismissed) {
      setAllDoneVisible(true);
      const t = setTimeout(dismiss, 3000);
      return () => clearTimeout(t);
    }
  }, [allDone]);

  if (dismissed || !athleteId) return null;

  return (
    <section className="mb-6">
      <Card className="border border-[#E0A458]/30 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] overflow-hidden shadow-lg shadow-black/20">
        <CardContent className="p-5">
          {allDoneVisible ? (
            <div className="flex items-center gap-3 py-2">
              <Sparkles className="h-6 w-6 text-[#E0A458] flex-shrink-0" />
              <div>
                <p className="font-montserrat font-bold text-white">You're off to a great start!</p>
                <p className="text-sm text-slate-400">Every step complete. Keep building the legacy.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#E0A458]">
                    Getting Started
                  </p>
                  <h3 className="font-montserrat font-bold text-lg text-white mt-0.5">
                    {completedCount} of {steps.length} steps complete
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-500 hover:text-white hover:bg-white/10 flex-shrink-0"
                  onClick={dismiss}
                  aria-label="Dismiss checklist"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 w-full bg-[#415A77]/30 rounded-full mb-5 overflow-hidden">
                <div
                  className="h-full bg-[#E0A458] rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${(completedCount / steps.length) * 100}%` }}
                />
              </div>

              {/* Step cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {steps.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div
                      key={step.id}
                      className={`rounded-xl border p-4 flex flex-col gap-3 transition-all duration-300 ${
                        step.done
                          ? "border-[#E0A458]/15 bg-[#E0A458]/5 opacity-50"
                          : "border-[#415A77]/40 bg-[#0D1B2A]/50"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        {step.done ? (
                          <CheckCircle2 className="h-5 w-5 text-[#E0A458] flex-shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                          <p
                            className={`font-semibold text-sm leading-snug ${
                              step.done ? "line-through text-slate-500" : "text-white"
                            }`}
                          >
                            {step.title}
                          </p>
                        </div>
                      </div>

                      {!step.done && (
                        <>
                          <p className="text-xs text-slate-400 leading-relaxed">
                            {step.description}
                          </p>
                          {step.action}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
